import React from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorPage403 from '@/app/routes/errorPage403';
import { Button } from '@/shared/components/Buttons';
import { ButtonVariant } from '@/shared/components/Buttons/types';
import { Tooltip } from '@/shared/components/Tooltip';
import InlineLayout from '@/shared/layout/inlineLayout';
import {
  useViewSubscriptionPlan,
  PLAN_NAV_ITEMS,
} from '../../hooks/useViewSubscriptionPlan';
import ViewSubscriptionPlanContent from './viewSubscriptionPlanContent';
import ViewSubscriptionPlanErrorScreen from './viewSubscriptionPlanErrorScreen';
import Loader from '@/app/routes/Loader';
import { useUserPrivileges } from '@shared/hooks/useUserPrivileges';
import { SUBSCRIPTION_ERROR_MESSAGES } from '../../errors/errorMessages';

const ViewSubscriptionPlanPage: React.FC<{ planId: string }> = ({ planId }) => {
  const navigate = useNavigate();
  const {
    plan,
    showErrorScreen,
    statusLabel,
    statusBadgeClasses,
    isLoading,
    accessError,
  } = useViewSubscriptionPlan(planId);
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  const planHeader = (
    <div className="flex items-center justify-between w-full gap-4">
      <div className="flex min-w-0 flex-1">
        <div className="flex min-w-0 flex-col gap-[4px]">
          <div className="flex min-w-0 flex-col items-start gap-1">
            <h2
              className="min-w-0 break-all font-medium text-neutral-10"
              style={{
                fontSize: '18px',
                lineHeight: '28px',
              }}
              data-test-id="Podgląd planu-value"
            >
              {plan?.planName ?? '—'}
            </h2>
            <span
              className={`px-3 py-1 rounded-[8px] text-sm font-medium flex items-center gap-2 ${statusBadgeClasses.container}`}
              style={{
                fontSize: '14px',
                lineHeight: '20px',
              }}
            >
              <div
                className={`rounded-full shrink-0 ${statusBadgeClasses.dot}`}
                style={{
                  width: '6px',
                  height: '6px',
                }}
              />
              {statusLabel}
            </span>
          </div>
        </div>
      </div>
      {privileges.subscriptionEdit ? (
        <Button
          label="Edytuj plan"
          variant={ButtonVariant.Primary}
          size="Medium"
          onClick={() => navigate(`/Plany/Edycja-planu/${planId}`)}
          className="!py-[10px] !px-[16px] !font-medium !text-[14px] hover:cursor-pointer shrink-0"
          style={{ borderRadius: '8px' }}
        />
      ) : (
        <Tooltip
          content={SUBSCRIPTION_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP}
          side="left"
          forceShow
          triggerWrapContent
        >
          <Button
            label="Edytuj plan"
            variant={ButtonVariant.Primary}
            size="Medium"
            disabled
            onClick={() => {}}
            className="!py-[10px] !px-[16px] !font-medium !text-[14px] shrink-0 !cursor-not-allowed"
            style={{ borderRadius: '8px' }}
          />
        </Tooltip>
      )}
    </div>
  );

  if (!privilegesLoading && !privileges.subscriptionView) {
    return <ErrorPage403 />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  if (accessError) {
    return <ErrorPage403 />;
  }

  return (
    <React.Fragment key={planId}>
      {showErrorScreen ? (
        <ViewSubscriptionPlanErrorScreen />
      ) : (
        <InlineLayout
          title="Podgląd planu"
          navItems={[...PLAN_NAV_ITEMS]}
          contentByNavId={{
            PodstawoweInformacje: (
              <ViewSubscriptionPlanContent
                plan={plan ?? null}
                activeTabId="PodstawoweInformacje"
              />
            ),
            CennikIRozliczenia: (
              <ViewSubscriptionPlanContent
                plan={plan ?? null}
                activeTabId="CennikIRozliczenia"
              />
            ),
            LimityUzywania: (
              <ViewSubscriptionPlanContent
                plan={plan ?? null}
                activeTabId="LimityUzywania"
              />
            ),
          }}
          header={planHeader}
          searchParamKey="tab"
          defaultActiveId="PodstawoweInformacje"
        />
      )}
    </React.Fragment>
  );
};

export default ViewSubscriptionPlanPage;
